import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../ThemeProvider';
import ResponsiveProp from './ResponsiveProp';
import ResponsiveWithMediaQueryStyleSheet from './ResponsiveWithMediaQueryStyleSheet';

/**
 * Responsive conditionally renders children based on whether the viewport matches the provided
 * min and max viewports.
 *
 * If enableMediaQueryStyleSheet themeOption is set to false in ThemeProvider, then in SSR,
 * like other viewport utilities, it treats the viewport as `xs` both in SSR itself and
 * during first hydration on the client side; then if the viewport is not `xs`, it re-renders
 * after hydration. This may cause a layout shift on devices other than the narrowest.
 *
 * If enableMediaQueryStyleSheet themeOption is set to true in ThemeProvider, then media query stylesheet
 * is used to hide and show children of `Responsive` within a View.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const Responsive = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    min = 'xs',
    max,
    inheritedStyles = [],
    children
  } = _ref;
  const {
    themeOptions: {
      enableMediaQueryStyleSheet
    }
  } = useTheme();
  if (enableMediaQueryStyleSheet) {
    return /*#__PURE__*/_jsx(ResponsiveWithMediaQueryStyleSheet, {
      inheritedStyles: inheritedStyles,
      min: min,
      max: max,
      ref: ref,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(ResponsiveProp, {
    min: min,
    max: max,
    children: children
  });
});
Responsive.displayName = 'Responsive';
Responsive.propTypes = {
  /**
   * To hide children of `Responsive` if the current viewport is smaller than `min`
   */
  min: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  /**
   * To hide children of `Responsive` if the current viewport is larger than `max`
   */
  max: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  /**
   * Styles to be inherited by `Responsive`.
   * It should be an array of style property names.
   * Note: This prop is only applicable when `enableMediaQueryStylesheet` is set to true in the `ThemeProvider`.
   */
  inheritedStyles: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired
};
export default Responsive;